import React, { useEffect, useState } from "react";
import { Grid, Paper, Tooltip, Typography, WithStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../auth/AuthProvider";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./ChangePasswordView.styles";
import * as UserPasswordAction from "../../../stores/user/UserPasswordAction";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useSnackbar } from "notistack";
import { RuleNames } from "../../components/PasswordValidator/PasswordValidator";
import configuration from "../../../utilities/configuration";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { FormPasswordField } from "../../components/FormComponents/FormPasswordField";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { useDispatchWithError } from "../../../hooks/useDispatchWithError";
import { useHistory } from "react-router-dom";

export interface IChangePasswordViewViewProps extends WithStyles<typeof styles> {}

export const ChangePasswordViewPure: React.FunctionComponent<IChangePasswordViewViewProps> = ({
    classes
}: IChangePasswordViewViewProps) => {
    const authContext = useAuthContext();
    const userId = authContext?.user?.id;
    const { t } = useTranslation();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [showPassword, setShowPassword] = useState(false);
    const [enableSave, setEnableSave] = useState(false);
    const [passwordValid, setPasswordValid] = useState(false);

    interface IPasswords {
        oldPassword: string;
        newPassword: string;
        confirmPassword: string;
    }
    interface IPasswordValid {
        newPassword: boolean;
        confirmPassword: boolean;
    }

    const [values, setValues] = React.useState<IPasswords>({
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const [validValues, setValidValues] = React.useState<IPasswordValid>({
        newPassword: false,
        confirmPassword: false
    });

    const errorsDefinition = {
        "400": "New password is empty",
        "401": "Incorrect password"
    };

    const [dispatchWithError, changePasswordError] = useDispatchWithError(
        UserPasswordAction.REQUEST_CHANGE_USER_PASSWORD_FINISHED,
        () => {
            enqueueSnackbar("Password changed", { variant: ToastStatusEnum.Info });
            history.push("/my-account/change-password/success");
        }, errorsDefinition
    );

    useEffect(() => {
        setEnableSave(values.oldPassword !== "" && validValues.newPassword && validValues.confirmPassword);
    }, [
        validValues.newPassword,
        validValues.confirmPassword,
        values.oldPassword,
        values.newPassword,
        values.confirmPassword
    ]);

    const handlePasswordChange = () => {
        const passwordsToSend = {
            oldPassword: values.oldPassword,
            newPassword: values.newPassword
        };
        dispatchWithError(UserPasswordAction.changeUserPassword(userId, passwordsToSend));
    };

    const handleChangePassword = (prop: keyof IPasswords) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues({ ...values, [prop]: event.target.value });
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleValidChange = (id: keyof IPasswordValid, isValid: boolean) => {
        setValidValues({ ...validValues, [id]: isValid });
        setPasswordValid(isValid && passwordValid);
    };

    const newPasswordRules: Array<RuleNames> = [
        ...(configuration?.cognitoPasswordMinimumLength ? ["minLength" as RuleNames] : []),
        ...(configuration?.cognitoRequireSymbols ? ["symbol" as RuleNames] : []),
        ...(configuration?.cognitoRequireNumbers ? ["number" as RuleNames] : []),
        ...(configuration?.cognitoRequireUppercase ? ["uppercase" as RuleNames] : []),
        ...(configuration?.cognitoRequireLowercase ? ["lowercase" as RuleNames] : [])
    ];

    const confirmPasswordRules: Array<RuleNames> = ["match"];

    const getSaveButttonTooltip = () => {
        if (enableSave) return "";
        if (values.oldPassword === "") return "The old password is empty.";
        return !validValues.newPassword
            ? "New password doesn't match the criteria."
            : "Confirmation password doesn't match the new password.";
    };

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("manageAccount.myAccount"), href: "/my-account" },
                    { title: t("manageAccount.changePassword.title") }
                ]}
            />
            <div className={classes.header}>
                <Typography variant="h2">{t("manageAccount.changePassword.title")}</Typography>
            </div>
            <Paper square className={classes.formBox}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormPasswordField
                            id="oldPassword"
                            onChange={handleChangePassword("oldPassword")}
                            showPassword={showPassword}
                            onChangeShowPassword={handleClickShowPassword}
                            password={values.oldPassword}
                            label="Old password"
                            helperText={changePasswordError?.message}
                            error={Boolean(changePasswordError)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormPasswordField
                            id="newPassword"
                            label="New password"
                            showPassword={showPassword}
                            onChangeShowPassword={handleClickShowPassword}
                            onChange={handleChangePassword("newPassword")}
                            validateParams={{
                                onChangeValid: handleValidChange
                            }}
                            rules={newPasswordRules}
                            password={values.newPassword}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormPasswordField
                            id="confirmPassword"
                            label="Confirm password"
                            showPassword={showPassword}
                            onChangeShowPassword={handleClickShowPassword}
                            onChange={handleChangePassword("confirmPassword")}
                            validateParams={{
                                onChangeValid: handleValidChange,
                                passwordToMatch: values.newPassword
                            }}
                            rules={confirmPasswordRules}
                            password={values.confirmPassword}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Tooltip title={getSaveButttonTooltip()}>
                            <span>
                                <BlueButton disabled={!enableSave} onClick={handlePasswordChange}>
                                    Save
                                </BlueButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>
        </CenteredContainer>
    );
};

export const ChangePasswordView = withStyles(styles)(ChangePasswordViewPure);
